<template>
  <div class="relative">
    <!-- Profile Trigger Button -->
    <Button
      @click="toggleMenu"
      text
      rounded
      class="relative flex items-center gap-1"
      :aria-label="t('profile.ViewProfile')"
      aria-haspopup="true"
      :aria-expanded="isMenuVisible"
    >
      <div class="relative">
        <Avatar 
          :label="(profileData?.fullName || 'U').charAt(0).toUpperCase()" 
          class="bg-amber-500 text-white cursor-pointer"
          shape="circle" 
        />
        <!-- Notification Badge: show global unread for Clients, booking-related for others -->
        <Badge
          v-if="(isClient && notificationStore.unreadCount > 0) || (!isClient && notificationStore.bookingRelatedUnreadCount > 0)"
          :value="isClient
            ? (notificationStore.unreadCount > 9 ? '9+' : notificationStore.unreadCount.toString())
            : (notificationStore.bookingRelatedUnreadCount > 9 ? '9+' : notificationStore.bookingRelatedUnreadCount.toString())"
          severity="danger"
          class="absolute -top-1 -right-1"
        />
      </div>
      <i class="pi pi-ellipsis-v text-gray-600"></i>
    </Button>

    <!-- Profile Menu Overlay -->
    <div
      v-if="isMenuVisible"
      class="fixed md:absolute left-1/2 -translate-x-1/2 md:left-auto md:translate-x-0 md:right-0 top-16 md:top-full mt-2 w-[90vw] md:w-96 bg-white rounded-lg shadow-lg border border-gray-200 z-[9999]"
      @click.stop
    >
      <div class="profile-content p-4">
        <!-- Loading State -->
        <div v-if="loading" class="flex justify-center items-center py-8">
          <ProgressSpinner style="width:50px;height:50px" strokeWidth="4" />
        </div>

        <!-- Profile Loaded -->
        <div v-else-if="profileData">
          <!-- Header -->
          <div class="flex items-center gap-3 mb-4 pb-4 border-b">
            <Avatar 
              :label="profileData.fullName.charAt(0).toUpperCase()" 
              class="bg-amber-500 text-white"
              shape="circle" 
              size="xlarge"
            />
            <div class="flex-1">
              <h3 class="text-lg font-semibold text-gray-800">{{ profileData.fullName }}</h3>
              <span class="inline-block px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full">
                {{ profileData.roleName }}
              </span>
            </div>
          </div>

          <!-- View Mode -->
          <div v-if="!isEditing" class="space-y-3">
            <div class="flex items-start gap-2">
              <i class="pi pi-user text-amber-600 mt-1"></i>
              <div class="flex-1">
                <label class="text-xs text-gray-500">{{ t('profile.Username') }}</label>
                <p class="text-sm font-medium text-gray-800">{{ profileData.userName }}</p>
              </div>
            </div>

            <div class="flex items-start gap-2">
              <i class="pi pi-envelope text-amber-600 mt-1"></i>
              <div class="flex-1">
                <label class="text-xs text-gray-500">{{ t('profile.Email') }}</label>
                <p class="text-sm font-medium text-gray-800">{{ profileData.email }}</p>
              </div>
            </div>

            <div class="flex items-start gap-2">
              <i class="pi pi-phone text-amber-600 mt-1"></i>
              <div class="flex-1">
                <label class="text-xs text-gray-500">{{ t('profile.Phone') }}</label>
                <p class="text-sm font-medium text-gray-800">{{ profileData.phone || t('common.NotSet') }}</p>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="flex gap-2 pt-3 border-t">
              <Button
                v-if="!isClient"
                :label="t('profile.EditProfile')"
                icon="pi pi-pencil"
                @click="startEdit"
                class="flex-1"
                size="small"
                outlined
              />
              <Button
                :label="t('profile.ChangePassword')"
                icon="pi pi-lock"
                @click="showPasswordDialog = true"
                :class="isClient ? 'w-full' : 'flex-1'"
                size="small"
                outlined
              />
            </div>

            <!-- My Schedule Button (Therapists only) -->
            <div v-if="isTherapist" class="pt-3 space-y-2">
              <Button
                :label="t('profile.MySchedule')"
                icon="pi pi-calendar"
                @click="navigateToMySchedule"
                class="w-full"
                size="small"
                outlined
              />
              <Button
                :label="t('profile.MyAbsences')"
                icon="pi pi-clock"
                @click="navigateToMyAbsences"
                class="w-full"
                size="small"
                outlined
              />
              <Button
                :label="t('profile.MyBookings')"
                icon="pi pi-book"
                @click="navigateToMyBookings"
                class="w-full relative"
                size="small"
                outlined
              >
                <template v-if="notificationStore.bookingRelatedUnreadCount > 0" #default>
                  <span class="flex items-center gap-2">
                    <i class="pi pi-book"></i>
                    <span>{{ t('profile.MyBookings') }}</span>
                    <span class="inline-flex items-center justify-center w-5 h-5 bg-amber-600 text-white text-xs font-bold rounded-full ml-auto">
                      {{ notificationStore.bookingRelatedUnreadCount > 9 ? '9+' : notificationStore.bookingRelatedUnreadCount }}
                    </span>
                  </span>
                </template>
              </Button>
              <Button
                :label="t('suggestion.MySuggestions')"
                icon="pi pi-lightbulb"
                @click="navigateToMySuggestions"
                class="w-full"
                size="small"
                outlined
              />
            </div>

            <!-- My Profile/Bookings Button (Clients only) -->
            <div v-if="isClient" class="pt-3 space-y-2">
              <Button
                :label="t('profile.MyProfile')"
                icon="pi pi-user"
                @click="navigateToMyProfile"
                class="w-full"
                size="small"
                outlined
              />
              <Button
                :label="t('profile.MyBookings')"
                icon="pi pi-book"
                @click="navigateToMyClientBookings"
                class="w-full relative"
                size="small"
                outlined
              >
                <template v-if="notificationStore.bookingRelatedUnreadCount > 0" #default>
                  <span class="flex items-center gap-2">
                    <i class="pi pi-book"></i>
                    <span>{{ t('profile.MyBookings') }}</span>
                    <span class="inline-flex items-center justify-center w-5 h-5 bg-amber-600 text-white text-xs font-bold rounded-full ml-auto">
                      {{ notificationStore.bookingRelatedUnreadCount > 9 ? '9+' : notificationStore.bookingRelatedUnreadCount }}
                    </span>
                  </span>
                </template>
              </Button>
              <Button
                :label="t('breadcrumb.MyPasses')"
                icon="pi pi-ticket"
                @click="navigateToMyPasses"
                class="w-full"
                size="small"
                outlined
              />
              <Button
                :label="t('suggestion.MySuggestions')"
                icon="pi pi-lightbulb"
                @click="navigateToMySuggestions"
                class="w-full"
                size="small"
                outlined
              />
            </div>

            <!-- Logout Button -->
            <div class="pt-3">
              <Button
                :label="t('auth.Logout')"
                icon="pi pi-sign-out"
                @click="handleLogout"
                class="w-full !bg-white !border-amber-500 !text-amber-600 hover:!bg-amber-50 hover:!border-amber-600"
                outlined
              />
            </div>
          </div>

          <!-- Edit Mode -->
          <div v-else class="space-y-3">
            <div class="flex flex-col gap-2">
              <label class="text-sm font-medium text-gray-700">{{ t('profile.FirstName') }}</label>
              <InputText 
                v-model="editForm.firstName" 
                :class="{ 'p-invalid': errors.firstName }"
              />
              <small v-if="errors.firstName" class="p-error">{{ errors.firstName }}</small>
            </div>

            <div class="flex flex-col gap-2">
              <label class="text-sm font-medium text-gray-700">{{ t('profile.LastName') }}</label>
              <InputText 
                v-model="editForm.lastName"
                :class="{ 'p-invalid': errors.lastName }"
              />
              <small v-if="errors.lastName" class="p-error">{{ errors.lastName }}</small>
            </div>

            <div class="flex flex-col gap-2">
              <label class="text-sm font-medium text-gray-700">{{ t('profile.Email') }}</label>
              <InputText 
                v-model="editForm.email"
                type="email"
                :class="{ 'p-invalid': errors.email }"
              />
              <small v-if="errors.email" class="p-error">{{ errors.email }}</small>
            </div>

            <div class="flex flex-col gap-2">
              <label class="text-sm font-medium text-gray-700">{{ t('profile.Phone') }}</label>
              <InputText 
                v-model="editForm.phone"
              />
            </div>

            <!-- Save/Cancel Buttons -->
            <div class="flex gap-2 pt-3 border-t">
              <Button
                :label="t('common.Save')"
                icon="pi pi-check"
                @click="saveProfile"
                :loading="saving"
                class="flex-1"
                size="small"
              />
              <Button
                :label="t('common.Cancel')"
                icon="pi pi-times"
                @click="cancelEdit"
                outlined
                class="flex-1"
                size="small"
                :disabled="saving"
              />
            </div>
          </div>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="text-center py-4">
          <i class="pi pi-exclamation-triangle text-4xl text-red-500 mb-2"></i>
          <p class="text-sm text-gray-600">{{ error }}</p>
          <Button
            :label="t('common.Retry')"
            @click="loadProfile"
            class="mt-3"
            size="small"
          />
        </div>
      </div>
    </div>

    <!-- Click outside handler -->
    <div
      v-if="isMenuVisible"
      class="fixed inset-0 z-[9998]"
      @click="isMenuVisible = false"
    ></div>

    <!-- Change Password Dialog -->
    <Dialog
      v-model:visible="showPasswordDialog"
      :header="t('profile.ChangePassword')"
      :modal="true"
      :style="{ width: '400px' }"
      :dismissableMask="true"
    >
      <div class="space-y-4">
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-gray-700">{{ t('profile.CurrentPassword') }}</label>
          <Password 
            v-model="passwordForm.currentPassword"
            :feedback="false"
            toggleMask
            :class="{ 'p-invalid': passwordErrors.currentPassword }"
          />
          <small v-if="passwordErrors.currentPassword" class="p-error">{{ passwordErrors.currentPassword }}</small>
        </div>

        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-gray-700">{{ t('profile.NewPassword') }}</label>
          <Password 
            v-model="passwordForm.newPassword"
            toggleMask
            :class="{ 'p-invalid': passwordErrors.newPassword }"
          />
          <small class="text-xs text-gray-500">{{ t('profile.PasswordHint') }}</small>
          <small v-if="passwordErrors.newPassword" class="p-error">{{ passwordErrors.newPassword }}</small>
        </div>

        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-gray-700">{{ t('profile.ConfirmPassword') }}</label>
          <Password 
            v-model="passwordForm.confirmPassword"
            :feedback="false"
            toggleMask
            :class="{ 'p-invalid': passwordErrors.confirmPassword }"
          />
          <small v-if="passwordErrors.confirmPassword" class="p-error">{{ passwordErrors.confirmPassword }}</small>
        </div>
      </div>

      <template #footer>
        <SaveCancelBar
          :can-save="false"
          :is-loading="changingPassword"
          :show-cancel="true"
          @save="changePassword"
          @cancel="closePasswordDialog"
        />
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAuthenticationStore } from '@/stores/authenticationStore';
import { useToastStore } from '@/stores/toastStore';
import { useNotificationStore } from '@/stores/notificationStore';
import UserService from '@/services/UserService';
import type { UserProfileResponse, UpdateUserProfileRequest, ChangePasswordRequest } from '@/types/user';
import SaveCancelBar from './SaveCancelBar.vue';
import { RoleConstants } from '@/constants/roleConstants';

const { t } = useI18n();
const router = useRouter();
const authStore = useAuthenticationStore();
const toastStore = useToastStore();
const notificationStore = useNotificationStore();

// Refs
const isMenuVisible = ref(false);
const loading = ref(false);
const saving = ref(false);
const changingPassword = ref(false);
const error = ref<string | null>(null);
const profileData = ref<UserProfileResponse | null>(null);
const isEditing = ref(false);
const showPasswordDialog = ref(false);

// Edit form
const editForm = reactive<UpdateUserProfileRequest>({
  firstName: '',
  lastName: '',
  email: '',
  phone: null,
  preferredLanguageLookupId: null
});

const errors = reactive<Record<string, string>>({});

// Password form
const passwordForm = reactive<ChangePasswordRequest>({
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
});

const passwordErrors = reactive<Record<string, string>>({});

// Computed
const isTherapist = computed(() => {
  return authStore.currentUser?.roleName?.toUpperCase() === RoleConstants.Therapist;
});

const isClient = computed(() => {
  return authStore.currentUser?.roleName?.toUpperCase() === RoleConstants.Client;
});

// Methods
const handleLogout = async () => {
  isMenuVisible.value = false;
  await authStore.logout();
};

const navigateToMySchedule = () => {
  isMenuVisible.value = false;
  router.push('/my-schedule');
};

const navigateToMyAbsences = () => {
  isMenuVisible.value = false;
  router.push('/my-absences');
};

const navigateToMyBookings = async () => {
  isMenuVisible.value = false;
  await notificationStore.markAllBookingNotificationsAsRead();
  router.push('/my-bookings');
};

const navigateToMySuggestions = () => {
  isMenuVisible.value = false;
  router.push('/my-suggestions');
};

const navigateToMyProfile = () => {
  isMenuVisible.value = false;
  router.push('/my-profile');
};

const navigateToMyClientBookings = async () => {
  isMenuVisible.value = false;
  await notificationStore.markAllBookingNotificationsAsRead();
  router.push('/my-client-bookings');
};

const navigateToMyPasses = () => {
  isMenuVisible.value = false;
  router.push('/my-passes');
};

// Methods
const toggleMenu = async() => {
  isMenuVisible.value = !isMenuVisible.value;
  if (isMenuVisible.value && !profileData.value) {
    await loadProfile();
  }
};

const loadProfile = async () => {
  loading.value = true;
  error.value = null;
  try {
    profileData.value = await UserService.getMyProfile();
  } catch (err: any) {
    error.value = err.message || t('profile.LoadError');
    toastStore.setToastErrorMessage(error.value || t('profile.LoadError'));
  } finally {
    loading.value = false;
  }
};

const startEdit = () => {
  if (profileData.value) {
    editForm.firstName = profileData.value.firstName;
    editForm.lastName = profileData.value.lastName;
    editForm.email = profileData.value.email;
    editForm.phone = profileData.value.phone;
    editForm.preferredLanguageLookupId = profileData.value.preferredLanguageLookupId;
  }
  Object.keys(errors).forEach(key => delete errors[key]);
  isEditing.value = true;
};

const cancelEdit = () => {
  isEditing.value = false;
  Object.keys(errors).forEach(key => delete errors[key]);
};

const validateProfile = (): boolean => {
  Object.keys(errors).forEach(key => delete errors[key]);

  if (!editForm.firstName.trim()) {
    errors.firstName = t('validation.Required');
  }
  if (!editForm.lastName.trim()) {
    errors.lastName = t('validation.Required');
  }
  if (!editForm.email.trim()) {
    errors.email = t('validation.Required');
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editForm.email)) {
    errors.email = t('validation.InvalidEmail');
  }

  return Object.keys(errors).length === 0;
};

const saveProfile = async () => {
  if (!validateProfile()) return;

  saving.value = true;
  try {
    profileData.value = await UserService.updateMyProfile(editForm);
    isEditing.value = false;
    toastStore.setToastSuccessMessage(t('profile.UpdateSuccess'));
  } catch (err: any) {
    const errorMsg = err.response?.data?.message || err.message || t('profile.UpdateError');
    toastStore.setToastErrorMessage(errorMsg);
  } finally {
    saving.value = false;
  }
};

const validatePassword = (): boolean => {
  Object.keys(passwordErrors).forEach(key => delete passwordErrors[key]);

  if (!passwordForm.currentPassword) {
    passwordErrors.currentPassword = t('validation.Required');
  }
  if (!passwordForm.newPassword) {
    passwordErrors.newPassword = t('validation.Required');
  } else if (passwordForm.newPassword.length < 8) {
    passwordErrors.newPassword = t('validation.PasswordTooShort');
  }
  if (!passwordForm.confirmPassword) {
    passwordErrors.confirmPassword = t('validation.Required');
  } else if (passwordForm.newPassword !== passwordForm.confirmPassword) {
    passwordErrors.confirmPassword = t('validation.PasswordMismatch');
  }

  return Object.keys(passwordErrors).length === 0;
};

const changePassword = async () => {
  if (!validatePassword()) return;

  changingPassword.value = true;
  try {
    await UserService.changePassword(passwordForm);
    toastStore.setToastSuccessMessage(t('profile.PasswordChangeSuccess'));
    closePasswordDialog();
  } catch (err: any) {
    const errorMsg = err.response?.data?.message || err.message || t('profile.PasswordChangeError');
    toastStore.setToastErrorMessage(errorMsg);
  } finally {
    changingPassword.value = false;
  }
};

const closePasswordDialog = () => {
  showPasswordDialog.value = false;
  passwordForm.currentPassword = '';
  passwordForm.newPassword = '';
  passwordForm.confirmPassword = '';
  Object.keys(passwordErrors).forEach(key => delete passwordErrors[key]);
};

onMounted(async () => {
  // Preload profile data when component is mounted
  await loadProfile();
});
</script>

<style scoped>
.profile-content {
  min-height: 200px;
}
</style>
